"use client";
import { useState } from "react";
import { Temporal } from "temporal-polyfill";
import { Button } from "../components/button";
import { Icon } from "../components/icon";
import { cn } from "../lib/utils";

interface MonthPickerProps {
  maxMonth?: Temporal.PlainDate;
  selectedMonth: Temporal.PlainDate | undefined;
  onMonthSelected: (value: Temporal.PlainDate | undefined) => void;
}

export function MonthPicker({ maxMonth, selectedMonth, onMonthSelected }: MonthPickerProps) {
  const now = Temporal.Now.plainDateISO();
  const [currentYear, setCurrentYear] = useState(now.year);

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const handleYearChange = (increment: number) => {
    const newYear = currentYear + increment;
    if (!maxMonth || newYear <= maxMonth.year) {
      setCurrentYear(newYear);
    }
  };

  const handleMonthSelect = (month: number) => {
    const selectedDate = Temporal.PlainDate.from({
      year: currentYear,
      month: month + 1,
      day: 1,
    });

    if (maxMonth && Temporal.PlainDate.compare(selectedDate, maxMonth) > 0) {
      return;
    }

    if (selectedMonth && selectedMonth.year === currentYear && selectedMonth.month === month + 1) {
      // deselect the selected month if it is already selected
      onMonthSelected(undefined);
    } else {
      // select the new month
      onMonthSelected(selectedDate);
    }
  };

  const isMonthSelected = (month: number) => {
    if (!selectedMonth) return false;
    return selectedMonth.year === currentYear && selectedMonth.month === month + 1;
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <button
            onClick={() => handleYearChange(-1)}
            className="p-2 rounded-full hover:bg-gray-100"
            type="button"
            aria-label="previous year"
          >
            <Icon name="arrow-left" iconSize="16px" />
          </button>
          <span className="text-lg font-medium">{currentYear}</span>
          <button
            onClick={() => handleYearChange(1)}
            className="p-2 rounded-full hover:bg-gray-100"
            disabled={maxMonth && currentYear >= maxMonth.year}
            type="button"
            aria-label="next year"
          >
            <Icon name="arrow-right" iconSize="16px" />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 p-4">
          {months.map((month, index) => {
            const isDisabled =
              maxMonth &&
              (currentYear > maxMonth.year || (currentYear === maxMonth.year && index + 1 > maxMonth.month));

            return (
              <Button
                key={month}
                variant="ghost"
                onClick={() => !isDisabled && handleMonthSelect(index)}
                disabled={isDisabled}
                className={cn(
                  "py-3 px-4 rounded-full text-center font-medium transition-colors",
                  isMonthSelected(index)
                    ? "bg-black text-white hover:bg-black"
                    : isDisabled
                      ? "text-textSubtle cursor-not-allowed"
                      : "hover:bg-backgroundLight",
                )}
              >
                {month}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
